import { UserRole } from '../types/auth.types';

/**
 * Permission keys — must match api/src/shared/constant/permissions.constant.ts
 */
export enum Permission {
  CATALOG_VIEW = 'catalog:view',
  CATALOG_CREATE = 'catalog:create',
  CATALOG_UPDATE = 'catalog:update',
  CATALOG_DELETE = 'catalog:delete',
  CATALOG_PUBLISH = 'catalog:publish',

  ORDER_VIEW = 'order:view',
  ORDER_VIEW_ALL = 'order:view_all',
  ORDER_UPDATE = 'order:update',
  ORDER_CAPTURE = 'order:capture',
  ORDER_REFUND = 'order:refund',
  ORDER_CANCEL = 'order:cancel',
  ORDER_FULFILL = 'order:fulfill',

  USER_VIEW = 'user:view',
  USER_VIEW_ALL = 'user:view_all',
  USER_UPDATE = 'user:update',
  USER_DELETE = 'user:delete',
  USER_MANAGE_ROLES = 'user:manage_roles',

  INVENTORY_VIEW = 'inventory:view',
  INVENTORY_UPDATE = 'inventory:update',
  INVENTORY_TRANSFER = 'inventory:transfer',

  DISCOUNT_VIEW = 'discount:view',
  DISCOUNT_CREATE = 'discount:create',
  DISCOUNT_UPDATE = 'discount:update',
  DISCOUNT_DELETE = 'discount:delete',

  VENDOR_VIEW = 'vendor:view',
  VENDOR_CREATE = 'vendor:create',
  VENDOR_UPDATE = 'vendor:update',
  VENDOR_DELETE = 'vendor:delete',

  PRINT_VIEW = 'print:view',
  PRINT_ASSIGN = 'print:assign',
  PRINT_UPDATE = 'print:update',
  PRINT_APPROVE = 'print:approve',

  REPORT_VIEW = 'report:view',
  REPORT_EXPORT = 'report:export',

  AUDIT_VIEW = 'audit:view',

  SETTINGS_VIEW = 'settings:view',
  SETTINGS_UPDATE = 'settings:update',
  SETTINGS_FEATURE_FLAGS = 'settings:feature_flags',
  SETTINGS_AUTH_POLICY = 'settings:auth_policy',
  SETTINGS_PAYMENT = 'settings:payment',

  SELLER_FINANCE_MANAGE = 'seller_finance:manage',
  SELLER_MANAGE = 'seller:manage',

  CREATOR_MANAGE = 'creator:manage',
  CREATOR_FINANCE_MANAGE = 'creator_finance:manage',

  DESIGN_VIEW = 'design:view',
  DESIGN_VIEW_OWN = 'design:view_own',
  DESIGN_CREATE = 'design:create',
  DESIGN_UPDATE = 'design:update',
  DESIGN_UPDATE_OWN = 'design:update_own',
  DESIGN_DELETE = 'design:delete',
  DESIGN_DELETE_OWN = 'design:delete_own',
  DESIGN_PUBLISH = 'design:publish',

  TAXONOMY_VIEW = 'taxonomy:view',
  TAXONOMY_MANAGE = 'taxonomy:manage',
  TAXONOMY_REVIEW_SUGGESTIONS = 'taxonomy:review_suggestions',
}

/** Mirrors api PermissionsService.rolePermissions */
const rolePermissions: Record<UserRole, Permission[]> = {
  [UserRole.OWNER]: Object.values(Permission),
  [UserRole.SUPER_ADMIN]: Object.values(Permission),

  [UserRole.DEPARTMENT_HEAD]: [
    Permission.CATALOG_VIEW,
    Permission.ORDER_VIEW,
    Permission.ORDER_VIEW_ALL,
    Permission.USER_VIEW,
    Permission.USER_VIEW_ALL,
    Permission.AUDIT_VIEW,
    Permission.REPORT_VIEW,
    Permission.REPORT_EXPORT,
    Permission.TAXONOMY_VIEW,
  ],

  [UserRole.ADMIN]: [
    Permission.CATALOG_VIEW,
    Permission.CATALOG_CREATE,
    Permission.CATALOG_UPDATE,
    Permission.CATALOG_PUBLISH,
    Permission.ORDER_VIEW,
    Permission.ORDER_VIEW_ALL,
    Permission.ORDER_UPDATE,
    Permission.ORDER_CAPTURE,
    Permission.ORDER_REFUND,
    Permission.ORDER_CANCEL,
    Permission.ORDER_FULFILL,
    Permission.USER_VIEW,
    Permission.USER_VIEW_ALL,
    Permission.USER_UPDATE,
    Permission.INVENTORY_VIEW,
    Permission.INVENTORY_UPDATE,
    Permission.INVENTORY_TRANSFER,
    Permission.DISCOUNT_VIEW,
    Permission.DISCOUNT_CREATE,
    Permission.DISCOUNT_UPDATE,
    Permission.DISCOUNT_DELETE,
    Permission.VENDOR_VIEW,
    Permission.VENDOR_UPDATE,
    Permission.PRINT_VIEW,
    Permission.PRINT_ASSIGN,
    Permission.PRINT_UPDATE,
    Permission.PRINT_APPROVE,
    Permission.REPORT_VIEW,
    Permission.REPORT_EXPORT,
    Permission.AUDIT_VIEW,
    Permission.SETTINGS_VIEW,
    Permission.SETTINGS_FEATURE_FLAGS,
    Permission.SELLER_FINANCE_MANAGE,
    Permission.SELLER_MANAGE,
    Permission.CREATOR_MANAGE,
    Permission.CREATOR_FINANCE_MANAGE,
    Permission.DESIGN_VIEW,
    Permission.TAXONOMY_VIEW,
    Permission.TAXONOMY_MANAGE,
    Permission.TAXONOMY_REVIEW_SUGGESTIONS,
  ],

  [UserRole.CREATOR]: [Permission.REPORT_VIEW],

  [UserRole.SPECIALIST]: [
    Permission.ORDER_VIEW,
    Permission.ORDER_VIEW_ALL,
    Permission.ORDER_UPDATE,
    Permission.ORDER_FULFILL,
    Permission.PRINT_VIEW,
    Permission.PRINT_ASSIGN,
    Permission.PRINT_UPDATE,
    Permission.INVENTORY_VIEW,
  ],

  [UserRole.SUPPORT]: [
    Permission.ORDER_VIEW,
    Permission.ORDER_VIEW_ALL,
    Permission.ORDER_UPDATE,
    Permission.USER_VIEW,
    Permission.USER_VIEW_ALL,
  ],

  [UserRole.SELLER]: [
    Permission.DESIGN_VIEW_OWN,
    Permission.DESIGN_CREATE,
    Permission.DESIGN_UPDATE_OWN,
    Permission.DESIGN_DELETE_OWN,
    Permission.DESIGN_PUBLISH,
    Permission.ORDER_VIEW,
    Permission.REPORT_VIEW,
  ],

  [UserRole.REGULAR]: [
    Permission.DESIGN_VIEW_OWN,
    Permission.DESIGN_CREATE,
    Permission.DESIGN_UPDATE_OWN,
    Permission.ORDER_VIEW,
  ],
};

export function getUserPermissions(
  roles: UserRole[],
  permissionOverrides?: Record<string, boolean> | null,
): Set<Permission> {
  const permissions = new Set<Permission>();
  roles.forEach((role) => {
    (rolePermissions[role] || []).forEach((perm) => permissions.add(perm));
  });
  if (permissionOverrides) {
    Object.entries(permissionOverrides).forEach(([perm, granted]) => {
      if (granted) {
        permissions.add(perm as Permission);
      } else {
        permissions.delete(perm as Permission);
      }
    });
  }
  return permissions;
}

export function hasPermission(
  roles: UserRole[],
  permission: Permission,
  permissionOverrides?: Record<string, boolean> | null,
): boolean {
  return getUserPermissions(roles, permissionOverrides).has(permission);
}

export function hasAnyPermission(
  roles: UserRole[],
  permissions: Permission[],
  permissionOverrides?: Record<string, boolean> | null,
): boolean {
  return permissions.some((perm) => hasPermission(roles, perm, permissionOverrides));
}

export function hasAllPermissions(
  roles: UserRole[],
  permissions: Permission[],
  permissionOverrides?: Record<string, boolean> | null,
): boolean {
  return permissions.every((perm) => hasPermission(roles, perm, permissionOverrides));
}

export function isOwner(roles: UserRole[]): boolean {
  return roles.includes(UserRole.OWNER);
}

export function isSuperAdmin(roles: UserRole[]): boolean {
  return roles.includes(UserRole.SUPER_ADMIN) || roles.includes(UserRole.OWNER);
}

/** Internal staff (owner dashboard shell) */
export function isAdmin(roles: UserRole[]): boolean {
  return roles.some((r) =>
    [
      UserRole.OWNER,
      UserRole.SUPER_ADMIN,
      UserRole.DEPARTMENT_HEAD,
      UserRole.ADMIN,
      UserRole.SPECIALIST,
      UserRole.SUPPORT,
    ].includes(r),
  );
}
